"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { ResponsiveModal } from "@/components/responsive-modal";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import useAuthModal from "../hooks/use-auth-modal";
import AuthForm from "./forms/auth-form";
import OtpForm from "./forms/otp-form";
import {
  type OnboardingStep,
  StudentTypeForm,
} from "./forms/student-type-form";

// Define the possible modal states
type ModalState = "SIGN_IN" | "SIGN_UP" | "VERIFICATION" | "ONBOARDING";

export const AuthModal = () => {
  const [modalState, setModalState] = useState<ModalState>("SIGN_IN");
  const [onboardingStep, setOnboardingStep] =
    useState<OnboardingStep>("learner");
  const [onboardingProgressIndex, setOnboardingProgressIndex] = useState(0);
  const [isSignUp, setIsSignUp] = useState(true);
  const [email, setEmail] = useState<string>("");
  const { isOpen, onClose, variant } = useAuthModal();
  const t = useI18n();

  const getTitleAndDescription = () => {
    switch (modalState) {
      case "SIGN_IN":
      case "SIGN_UP":
        return {
          title: isSignUp ? t("auth.createAccount") : t("auth.signIn"),
          description: isSignUp
            ? t("auth.signUpWithGoogle")
            : t("auth.signInWithGoogle"),
        };
      case "VERIFICATION":
        return {
          title: t("auth.verifyEmail"),
          description: t("auth.verifyEmailDescription").replace(
            "{email}",
            email,
          ),
        };
      case "ONBOARDING":
        switch (onboardingStep) {
          case "health":
            return {
              title: "What type of health student are you?",
              description:
                "This information is used to personalize your experience",
            };
          case "firstResponder":
            return {
              title: "What type of first responder are you?",
              description:
                "This information is used to personalize your experience",
            };
          case "referral":
            return {
              title: "Where did you hear about us?",
              description:
                "This helps our marketing team find other people like you",
            };
          default:
            return {
              title: "What type of learner are you?",
              description:
                "This information is used to personalize your experience",
            };
        }
      default:
        return { title: "", description: "" };
    }
  };

  const handleModalClose = () => {
    onClose();
    setModalState("SIGN_IN");
    setIsSignUp(true);
    setOnboardingStep("learner");
    setOnboardingProgressIndex(0);
  };

  const reset = () => {
    setModalState("SIGN_IN");
  };

  const handleAuthSuccess = (userEmail: string) => {
    setEmail(userEmail);
    setModalState("VERIFICATION");
  };

  const handleVerificationSuccess = () => {
    if (isSignUp) {
      setModalState("ONBOARDING");
      setOnboardingStep("learner");
      setOnboardingProgressIndex(0);
    } else {
      window.location.reload();
      handleModalClose();
    }
  };

  const handleOnboardingStepChange = (
    step: OnboardingStep,
    progressIndex: number,
  ) => {
    setOnboardingStep(step);
    setOnboardingProgressIndex(progressIndex);
  };

  const handleOnboardingSuccess = () => {
    handleModalClose();
  };

  useEffect(() => {
    if (isOpen && variant === "onboarding") {
      setModalState("ONBOARDING");
      setOnboardingStep("learner");
      setOnboardingProgressIndex(0);
      setIsSignUp(true);
    }
  }, [isOpen, variant]);

  const renderContent = () => {
    switch (modalState) {
      case "SIGN_IN":
      case "SIGN_UP":
        return (
          <AuthForm
            isSignUp={isSignUp}
            setIsSignUp={setIsSignUp}
            onSuccess={handleAuthSuccess}
          />
        );
      case "VERIFICATION":
        return (
          <OtpForm
            email={email}
            onSuccess={handleVerificationSuccess}
            reset={reset}
          />
        );
      case "ONBOARDING":
        return (
          <StudentTypeForm
            onSuccess={handleOnboardingSuccess}
            onStepChange={handleOnboardingStepChange}
          />
        );
      default:
        return null;
    }
  };

  const { title, description } = getTitleAndDescription();
  const headerAddon =
    modalState === "ONBOARDING" ? (
      <OnboardingProgressIndicator
        activeIndex={onboardingProgressIndex}
        needs3Steps={
          onboardingStep === "health" || onboardingStep === "firstResponder"
        }
      />
    ) : undefined;

  return (
    <ResponsiveModal
      className={cn("max-w-lg", modalState === "ONBOARDING" && "max-w-3xl")}
      title={title}
      titleClassName="text-2xl"
      description={description}
      form={renderContent()}
      open={isOpen}
      onOpenChange={handleModalClose}
      hideCloseIcon={
        modalState === "VERIFICATION" || modalState === "ONBOARDING"
      }
      headerAddon={headerAddon}
    />
  );
};

const OnboardingProgressIndicator = ({
  activeIndex,
  needs3Steps = false,
}: {
  activeIndex: number;
  needs3Steps?: boolean;
}) => {
  return (
    <div className="flex items-center gap-2">
      {(needs3Steps ? ["a", "b", "c"] : ["a", "b"]).map((id, index) => (
        <div
          key={`auth-step-${id}`}
          className={cn(
            "relative h-1 overflow-hidden rounded-lg",
            index === activeIndex ? "w-8" : "w-4",
          )}
        >
          <div className="absolute inset-0 bg-border" />
          <motion.div
            className="absolute inset-0 bg-brand-primary"
            initial={{ x: "-100%" }}
            animate={{ x: index <= activeIndex ? "0%" : "-100%" }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </div>
      ))}
    </div>
  );
};
